import * as CL from '@design-system/component-library';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { dsClass } from '../../common/constants/dsClasses.js';
import { cancelMsg, confirmMsg, t } from '../../common/i18n/index.js';
import { type ReceiverType, ReceiverTypes } from '../../common/utils/billingAccountUtils.js';
import { getCustomerPrimaryAddress } from '../../common/utils/customerAddressUtilsCoCo.js';
import { DEFAULT_COUNTRY_CODE } from '../../common/utils/validationUtils.js';
import { BillingAccount } from '../../generated/api/billingAccount.js';
import { BillingAccountFieldset } from '../BillingAccount/BillingAccountFieldset.js';
import { DeliveryMethodFieldsetCoCo, deliveryMethodCocoDefaultValues } from './DeliveryMethodFieldsetCoCo.js';
import type { BillingAccountCocoFormValues } from './billingAccountCocoFormModel.js';
import type { BillChannel } from '../../generated/api/billChannel.js';
import type { Contact } from '../../generated/api/contact.js';
import type { CustomerResponse } from '../../generated/api/customerResponse.js';

import './BillingAccountForm.scss';

export interface BillingAccountFormCoCoProps {
  billChannels: BillChannel[];
  contacts: Contact[];
  customer?: CustomerResponse;
  initialValues?: {
    billingAccount?: Partial<BillingAccountCocoFormValues['billingAccount']>;
    billReceiverSelection?: ReceiverType;
  };
  isSaving?: boolean;
  onSubmit: (values: BillingAccountCocoFormValues) => void | Promise<void>;
  onCancel?: () => void;
  onDirtyState?: (isDirty: boolean) => void;
}

const getDefaultValues = (customer?: CustomerResponse, billChannels?: BillChannel[]): BillingAccountCocoFormValues => {
  const address = customer ? getCustomerPrimaryAddress(customer) : undefined;
  const payerName = customer?.name ?? '';

  return {
    billingAccount: {
      payerName,
      payerNameExtension: '',
      payerAddress: {
        countryCode: address?.country ?? DEFAULT_COUNTRY_CODE,
        line1: address?.street1 ?? '',
        postOffice: address?.city ?? '',
        postalCode: address?.postCode ?? '',
      },
      billingContactId: '',
      billFormatType: BillingAccount.BillFormatTypeEnum.CONSOLIDATED_INVOICE_WITH_ITEMIZATION,
      billingAccountName: payerName,
      ...deliveryMethodCocoDefaultValues(billChannels),
      billingAccountExtensionName: '',
      billReceiverId: '',
      customerReference1: '',
      customerReference2: '',
      customerReference3: '',
      customerReference4: '',
    },
    billReceiverSelection: ReceiverTypes.SAME_AS_CONTACT,
  };
};

// Contact creation moves to a dedicated API; the dropdown option stays visible but is inert until then.
const noop = () => undefined;

export const BillingAccountFormCoCo = ({
  billChannels,
  contacts,
  customer,
  initialValues,
  isSaving = false,
  onSubmit,
  onCancel,
  onDirtyState,
}: BillingAccountFormCoCoProps) => {
  const methods = useForm<BillingAccountCocoFormValues>({
    defaultValues: {
      ...getDefaultValues(customer, billChannels),
      ...initialValues,
      billingAccount: {
        ...getDefaultValues(customer, billChannels).billingAccount,
        ...initialValues?.billingAccount,
      },
    },
  });
  const { formState, handleSubmit } = methods;

  useEffect(() => {
    onDirtyState?.(formState.isDirty);
  }, [formState.isDirty, onDirtyState]);

  return (
    <FormProvider {...methods}>
      <div>
        <BillingAccountFieldset billChannels={billChannels} contacts={contacts} onCreateBillingContact={noop} />
        <DeliveryMethodFieldsetCoCo
          billChannels={billChannels}
          contacts={contacts}
          onCreateBillReceiverContact={noop}
        />
        <div className={`${dsClass.DISPLAY_FLEX} ${dsClass.JUSTIFY_CONTENT_FLEX_END}`}>
          <CL.Button className={dsClass.MARGIN_RIGHT_4} loading={isSaving} onClick={handleSubmit(onSubmit)}>
            {t.QVYK(confirmMsg)}
          </CL.Button>
          <CL.Button type="button" color="light" disabled={isSaving} onClick={onCancel}>
            {t.B2V1(cancelMsg)}
          </CL.Button>
        </div>
      </div>
    </FormProvider>
  );
};
